import { Region } from "@/gql/graphql";
import { useSuspenseGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { allCitiesQuery } from "@/app/Reports/Reports/Components/ReportBookings/allCitiesQuery";
import { Suspense, useMemo } from "react";
import { groupBy } from "@/lib/Utils/groupBy";
import { SelectInput } from "@/lib/Components/Form/Inputs/SelectInput";
import { useTranslations } from "use-intl";
import { Skeleton } from "@/components/ui/skeleton";

const REGIONS_WITHOUT_STATES = [Region.Nz, Region.Jp, Region.Tw];

type Props = {
  name: string;
  label: string;
  className?: string;
  region: Region;
};
export function WaitlistLocationInput(props: Props) {
  return (
    <Suspense
      fallback={
        <div>
          <Skeleton />
        </div>
      }
    >
      <WaitlistLocationInputComp {...props} />
    </Suspense>
  );
}

function WaitlistLocationInputComp({ name, label, region, className }: Props) {
  const t = useTranslations("city");
  const { data } = useSuspenseGqlQuery(allCitiesQuery, {});

  const cities = useMemo(() => {
    return data?.allCities.filter((city) => city.region === region) ?? [];
  }, [data, region]);

  // Get all states for the region
  const states = useMemo(() => {
    const statesSet = new Set(cities.map((city) => city.state));
    return Array.from(statesSet).sort();
  }, [cities]);

  // Group cities by state
  const groupedCities = useMemo(() => {
    return groupBy(cities, (city) => city.state);
  }, [cities]);

  // Check if this region should show state selection
  const showStateSelection = !REGIONS_WITHOUT_STATES.includes(region);

  const options = useMemo(() => {
    const optionGroups = [];

    // Add states group if needed
    if (showStateSelection) {
      optionGroups.push({
        label: "States",
        options: states.map((state) => ({
          label: t(`state.${state}`),
          value: `state:${state}`,
        })),
      });
    }

    // Add city groups by state
    states.forEach((stateCode) => {
      const stateCities = groupedCities[stateCode] || [];
      if (stateCities.length > 0) {
        optionGroups.push({
          label: t(`state.${stateCode}`),
          options: stateCities
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((city) => ({
              label: city.name,
              value: `city:${city.id}`,
            })),
        });
      }
    });

    return optionGroups;
  }, [showStateSelection, states, groupedCities, t]);

  return (
    <SelectInput
      name={name}
      label={label}
      className={className}
      options={options}
    />
  );
}
